import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Building2, 
  AlertCircle, 
  Loader2,
  RefreshCw,
  Plus,
  Link as LinkIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { BeneficiaryCard } from "@/components/BeneficiaryCard";
import { 
  getApplication, 
  getApplicationBeneficiary, 
  getVerifiedBeneficiariesByCompany,
  linkExistingBeneficiary 
} from "@/lib/api";
import { toast } from "sonner";

export default function BankDetailsManagement() {
  const { appId } = useParams();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState(null);
  const [beneficiary, setBeneficiary] = useState(null);
  const [existingBeneficiaries, setExistingBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  
  // Link dialog
  const [linkDialog, setLinkDialog] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  useEffect(() => {
    loadData();
  }, [appId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const appData = await getApplication(appId);
      setApplication(appData);
      
      const benData = await getApplicationBeneficiary(appId);
      setBeneficiary(benData.beneficiary);
      
      // Load existing verified beneficiaries for this company
      if (appData.company_name) {
        try {
          const existing = await getVerifiedBeneficiariesByCompany(appData.company_name);
          setExistingBeneficiaries(existing);
        } catch (e) {
          // No existing beneficiaries
          setExistingBeneficiaries([]);
        }
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load application data");
    } finally {
      setLoading(false);
    }
  };

  const handleLinkBeneficiary = async () => {
    if (!selectedBeneficiary) return;
    
    try {
      setLinking(true);
      await linkExistingBeneficiary(appId, selectedBeneficiary.beneficiary_id, "admin@simplyfunded.com");
      toast.success("Beneficiary linked successfully");
      setLinkDialog(false);
      setSelectedBeneficiary(null);
      loadData();
    } catch (error) {
      console.error("Failed to link beneficiary:", error);
      toast.error("Failed to link beneficiary");
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Application Not Found</h2>
        <Button onClick={() => navigate("/")} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Deals
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="bank-details-management-page">
      <PageHeader
        title="Bank Details Management"
        description={`${application.company_name} (${application.application_id})`}
        breadcrumb={
          <Link 
            to={`/workflow/${appId}`} 
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Workflow
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Current Status */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Current Bank Details</CardTitle>
                <StatusBadge status={application.bank_details_status} />
              </div>
            </CardHeader>
            <CardContent>
              {/* Verified */}
              {application.bank_details_status === "verified" && beneficiary && (
                <div className="space-y-4">
                  <BeneficiaryCard beneficiary={beneficiary} />
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/workflow/${appId}`)}
                    >
                      Continue to Workflow
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setLinkDialog(true)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Replace Bank Details
                    </Button>
                  </div>
                </div>
              )}

              {/* Pending Approval */}
              {application.bank_details_status === "pending_approval" && (
                <div className="space-y-4">
                  {beneficiary && (
                    <BeneficiaryCard beneficiary={beneficiary} showFullDetails />
                  )}
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800">Awaiting Credit Approval</AlertTitle>
                    <AlertDescription className="text-amber-700">
                      These bank details are pending review by the credit team.
                    </AlertDescription>
                  </Alert>
                  <Button asChild>
                    <Link to="/credit-approvals">Go to Credit Approvals</Link>
                  </Button>
                </div>
              )}

              {/* Not Provided */}
              {application.bank_details_status === "not_provided" && (
                <div className="space-y-4">
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Bank Details</h3>
                    <p className="text-muted-foreground mb-4">
                      Bank details have not been submitted for this application.
                    </p>
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => navigate(`/add-bank-details/${appId}`)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Bank Details
                    </Button>
                    {existingBeneficiaries.length > 0 && (
                      <Button variant="outline" onClick={() => setLinkDialog(true)}>
                        <LinkIcon className="w-4 h-4 mr-2" />
                        Link Existing
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Rejected */}
              {application.bank_details_status === "rejected" && (
                <div className="space-y-4">
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800">Bank Details Rejected</AlertTitle>
                    <AlertDescription className="text-red-700">
                      {application.rejection_reason || "The submitted bank details were rejected."}
                    </AlertDescription>
                  </Alert>
                  <div className="flex gap-3">
                    <Button onClick={() => navigate(`/add-bank-details/${appId}`)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Submit New Bank Details
                    </Button>
                    {existingBeneficiaries.length > 0 && (
                      <Button variant="outline" onClick={() => setLinkDialog(true)}>
                        <LinkIcon className="w-4 h-4 mr-2" />
                        Link Existing
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Existing Beneficiaries */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Verified Accounts</CardTitle>
              <CardDescription>
                Previously verified bank accounts for this company
              </CardDescription>
            </CardHeader>
            <CardContent>
              {existingBeneficiaries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No previously verified accounts found
                </p>
              ) : (
                <div className="space-y-3">
                  {existingBeneficiaries.map((ben) => (
                    <BeneficiaryCard
                      key={ben.beneficiary_id}
                      beneficiary={ben}
                      compact
                      selected={beneficiary?.beneficiary_id === ben.beneficiary_id}
                      onSelect={() => {
                        setSelectedBeneficiary(ben);
                        setLinkDialog(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Link Existing Beneficiary Dialog */}
      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link Existing Beneficiary</DialogTitle>
            <DialogDescription>
              Select a previously verified bank account for {application.company_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
            {existingBeneficiaries.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No verified beneficiaries available. Please add new bank details.
                </p>
              </div>
            ) : (
              existingBeneficiaries.map((ben) => (
                <BeneficiaryCard
                  key={ben.beneficiary_id}
                  beneficiary={ben}
                  compact
                  selected={selectedBeneficiary?.beneficiary_id === ben.beneficiary_id}
                  onSelect={() => setSelectedBeneficiary(ben)}
                />
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleLinkBeneficiary}
              disabled={!selectedBeneficiary || linking}
              data-testid="confirm-link-btn"
            >
              {linking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Link Selected Beneficiary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
